import { useEffect, useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, Scale } from "lucide-react";
import { cn } from "../lib/cn";
import { useDocumentLocale, useMicrocopy, type MicrocopyKey } from "../lib/microcopy";
import { Badge } from "./badge";
import { InlineAlert } from "./feedback";
import { MarkdownBody } from "./markdown";

/**
 * A letter a step would send, as the server renders it from the step's own template and the file (release 1.6; CL-01 §9, Conditions 3.1, 3.2
 * and 3.10): the text the step will record, the addressee, and whether it has legal effect.
 */
export interface StepLetterView {
  source: string; templateKey: string; kind: string; subject: string; subjectAr: string; body: string; bodyAr: string;
  recipientName: string; recipientNameAr?: string | null; legalEffect: boolean; legalBasis?: string | null; responseDays?: number | null; responseWorkingDays: boolean;
  /** The provisions it cites that the Department has not yet verified (condition 2.13), named before the step is confirmed; null when none. */
  citations?: { unverified: Array<{ key: string; name: string; nameAr: string; cited: string; citedAr: string }>; writtenOut: string[] } | null;
}

/** The server's answer for a step: its letters, and the fingerprint its confirmation carries once they are read (null when it sends none). */
export interface StepLettersAnswer { letters: StepLetterView[]; fingerprint?: string | null; stepAvailable?: boolean }

export interface LetterPreview {
  letters: StepLetterView[];
  /** The step may be confirmed: the letters on screen are those of the step as it now stands – at once for a step that sends none. */
  shown: boolean;
  /** What the confirmation carries; null when the step sends no letter. */
  fingerprint: string | null;
  loading: boolean;
  error: unknown;
}

/**
 * The letters a step would send, read from the server for the step as it now stands; nothing is sent or saved. `step` names the step (its
 * letters stay on screen while new wording is rendered), `request` is what the step will record (the officer's note), and `read` asks the
 * server for it. A change of wording is read once the typing stops.
 */
export function useLetterPreview<R>(step: string | null, request: R | null, read: (request: R) => Promise<StepLettersAnswer>): LetterPreview {
  const current = step === null || request === null ? null : JSON.stringify([step, request]);
  const [settled, setSettled] = useState(current);
  useEffect(() => {
    if (current === settled) return;
    if (!current || !settled || JSON.parse(current)[0] !== JSON.parse(settled)[0]) { setSettled(current); return; }
    const id = window.setTimeout(() => setSettled(current), 450);
    return () => window.clearTimeout(id);
  }, [current, settled]);
  const asked = settled ? (JSON.parse(settled) as [string, R]) : null;
  const query = useQuery({
    queryKey: ["step-letter-preview", asked?.[0] ?? null, settled],
    queryFn: () => read(asked![1]),
    enabled: !!asked,
    staleTime: 0,
    retry: false,
    placeholderData: (previous, previousQuery) => (asked && previousQuery?.queryKey[1] === asked[0] ? previous : undefined),
  });
  const sameStep = !!current && !!asked && JSON.parse(current)[0] === asked[0];
  const fresh = !!current && settled === current && query.isSuccess && !query.isPlaceholderData && !query.isFetching;
  const letters = current && query.data ? query.data.letters : [];
  // Whether a step sends letters does not turn on the officer's wording: a step that sends none never waits for it to be rendered.
  const shown = !!current && !query.isError && !!query.data && sameStep && (fresh || query.data.letters.length === 0);
  return { letters, shown, fingerprint: shown && letters.length > 0 ? query.data?.fingerprint ?? null : null, loading: !!current && !query.data && !query.isError, error: current ? query.error : null };
}

const SOURCE: Record<string, MicrocopyKey> = {
  "completion-request": "letterCompletionRequest",
  "documents-to-follow": "letterDocumentsToFollow",
  "implicit-rejection": "letterImplicitRejection",
  "review-extended": "letterReviewExtended",
  "workflow-action": "letterWorkflowAction",
};

/** Each letter a step sends, in full: what it is, its subject, addressee and legal effect, its English and Arabic text. Nothing when there is none. */
export function StepLetterSheets({ preview, hint, className }: { preview: LetterPreview; hint?: string; className?: string }) {
  const m = useMicrocopy();
  const ar = useDocumentLocale() === "ar";
  const id = useId();
  if (preview.error) return <InlineAlert tone="danger" title={m("stepLettersFailed")} className={className} />;
  if (preview.loading) return <p className={cn("text-xs text-muted-foreground", className)}>{m("stepLettersLoading")}</p>;
  if (preview.letters.length === 0) return null;
  return (
    <section className={cn("space-y-3", className)} aria-labelledby={id} data-step-letters={preview.letters.length}>
      <div>
        <p id={id} className="flex items-center gap-1.5 text-sm font-semibold text-aeblack-950"><Mail className="size-4 text-sand-700" aria-hidden /> {preview.letters.length === 1 ? m("stepLetterOne") : m("stepLettersTitle", { count: preview.letters.length })}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint ?? m("stepLettersHint")}</p>
      </div>
      {preview.letters.map((l, i) => (
        <article key={`${l.source}:${l.templateKey}:${i}`} className="overflow-hidden rounded-lg border border-sand-200 bg-white" data-step-letter={l.source}>
          <header className="space-y-1 border-b border-sand-100 bg-sand-50/60 px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{SOURCE[l.source] ? m(SOURCE[l.source]!) : l.templateKey}</p>
            <p className="text-sm font-semibold text-aeblack-950" dir="auto">{ar ? l.subjectAr : l.subject}</p>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-sand-800">
              <span dir="auto">{m("stepLetterTo", { name: ar && l.recipientNameAr ? l.recipientNameAr : l.recipientName })}</span>
              {l.legalEffect ? <Badge size="sm" variant="warning"><Scale aria-hidden /> {m("stepLetterLegalEffect")}</Badge> : <Badge size="sm" variant="outline">{m("stepLetterCourtesy")}</Badge>}
            </div>
          </header>
          {l.citations && (l.citations.unverified.length > 0 || l.citations.writtenOut.length > 0) ? (
            <div className="border-b border-sand-100 px-3 py-2.5">
              <InlineAlert tone="warning" title={m("stepLetterUnverified")}>
                <p className="text-xs">{m("stepLetterUnverifiedHint")}</p>
                <ul className="mt-1.5 space-y-0.5 text-xs" data-unverified-provisions={l.citations.unverified.length}>
                  {l.citations.unverified.map((c) => <li key={c.key} dir="auto">{ar ? c.citedAr : c.cited} · <span className="text-muted-foreground">{ar ? c.nameAr : c.name}</span></li>)}
                  {l.citations.writtenOut.map((a) => <li key={a} dir="auto">{a}</li>)}
                </ul>
              </InlineAlert>
            </div>
          ) : null}
          <div className="grid divide-y divide-sand-100 md:grid-cols-2 md:divide-x md:divide-y-0 rtl:md:divide-x-reverse">
            {([["en", "ltr", m("stepLetterEnglish"), l.subject, l.body], ["ar", "rtl", m("stepLetterArabic"), l.subjectAr, l.bodyAr]] as const).map(([lang, dir, label, subject, body]) => (
              <div key={lang} className="min-w-0 px-3 py-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{label}</p>
                <div lang={lang} dir={dir}>
                  <p className="mt-1 text-sm font-medium text-aeblack-950">{subject}</p>
                  <div className="mt-1 max-h-72 overflow-y-auto text-sm" tabIndex={0} role="region" aria-label={`${label} · ${subject}`}><MarkdownBody>{body}</MarkdownBody></div>
                </div>
              </div>
            ))}
          </div>
        </article>
      ))}
    </section>
  );
}
